import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { MediaUploader } from '../../components/MediaUploader';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const PROPERTY_TYPES = ['House','Condominium','Land','Commercial','Townhouse','Apartment'];
const STATUSES = ['Available', 'New', 'Hot', 'Sold'];

const EMPTY = {
  title: '', type: 'House', transaction_type: 'For Sale', price: 0, location: '', address: '',
  bedrooms: 0, bathrooms: 0, floor_area: 0, lot_area: 0, parking: 0, year_built: 2024,
  featured: false, status: 'Available', description: '', features: [],
  gallery: [], video_url: '', videos: [], agent_id: ''
};

// Strip a leading bullet marker (-, *, •) and surrounding whitespace from a line.
const cleanFeature = (line) => line.replace(/^[\s]*[-*•]+[\s]*/, '').trim();

export default function AdminPropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [featuresText, setFeaturesText] = useState('');
  const [agents, setAgents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(id);

  useEffect(() => {
    api.get('/admin/agents').then(r => setAgents(r.data || []));
    api.get('/locations').then(r => setLocations(r.data || []));
    if (isEdit) {
      api.get('/properties/' + id).then(r => {
        const features = r.data.features || [];
        // Backfill: older listings only have the single video_url.
        const videos = (r.data.videos && r.data.videos.length) ? r.data.videos : (r.data.video_url ? [r.data.video_url] : []);
        setForm({ ...EMPTY, ...r.data, features, videos });
        setFeaturesText(features.join('\n'));
      });
    }
  }, [id]);

  const onFeaturesChange = (text) => {
    setFeaturesText(text);
    set('features', text.split('\n').map(cleanFeature).filter(Boolean));
  };

  // Keep the current value selectable even if it was later removed from the list.
  const locationNames = locations.map(l => l.name);
  const locationOptions = form.location && !locationNames.includes(form.location)
    ? [form.location, ...locationNames]
    : locationNames;

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const videos = form.videos || [];
      const payload = { ...form, videos, video_url: videos[0] || '', price: parseFloat(form.price) || 0, bedrooms: parseInt(form.bedrooms) || 0, bathrooms: parseInt(form.bathrooms) || 0, floor_area: parseFloat(form.floor_area) || 0, lot_area: parseFloat(form.lot_area) || 0, parking: parseInt(form.parking) || 0, year_built: parseInt(form.year_built) || 2024 };
      if (isEdit) await api.put('/admin/properties/' + id, payload);
      else await api.post('/admin/properties', payload);
      toast({ title: isEdit ? 'Updated!' : 'Created!', description: 'Property saved successfully.' });
      navigate('/admin/properties');
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl">
      <Link to="/admin/properties" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#DC2626] mb-4"><ArrowLeft className="w-4 h-4" /> Back to properties</Link>
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">{isEdit ? 'Edit Property' : 'New Property'}</h1>
      <p className="text-sm text-neutral-500 mb-6">{isEdit ? 'Update listing details and media.' : 'Add a new property listing.'}</p>

      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Title *</label>
              <Input required value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Modern 4BR House in Cebu City" />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Property Type</label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Transaction</label>
              <Select value={form.transaction_type} onValueChange={(v) => set('transaction_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="For Sale">For Sale</SelectItem><SelectItem value="For Rent">For Rent</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Price (₱)</label>
              <Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Location</label>
              <Select value={form.location} onValueChange={(v) => set('location', v)}>
                <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locationOptions.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Assigned Advisor</label>
              <Select value={form.agent_id} onValueChange={(v) => set('agent_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select advisor" /></SelectTrigger>
                <SelectContent>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Full Address</label>
              <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Street, Barangay, City" />
            </div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Switch checked={form.featured} onCheckedChange={(v) => set('featured', v)} />
              <label className="text-sm font-medium text-neutral-700">Show in Featured Listings</label>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Property Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Bedrooms</label><Input type="number" value={form.bedrooms} onChange={(e) => set('bedrooms', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Bathrooms</label><Input type="number" value={form.bathrooms} onChange={(e) => set('bathrooms', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Floor Area (sqm)</label><Input type="number" value={form.floor_area} onChange={(e) => set('floor_area', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Lot Area (sqm)</label><Input type="number" value={form.lot_area} onChange={(e) => set('lot_area', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Parking</label><Input type="number" value={form.parking} onChange={(e) => set('parking', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Year Built</label><Input type="number" value={form.year_built} onChange={(e) => set('year_built', e.target.value)} /></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Description & Features</h2>
          <Textarea rows={6} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Detailed description of the property..." />
          <div className="mt-4">
            <label className="text-sm font-medium text-neutral-700 mb-1 block">Features (one per line)</label>
            <Textarea
              rows={5}
              value={featuresText}
              onChange={(e) => onFeaturesChange(e.target.value)}
              placeholder={"One feature per line, e.g.\n24/7 Security\nGated Community\nSwimming Pool"}
            />
            <p className="mt-1 text-xs text-neutral-500">Each line becomes its own bullet. Leading “-”, “*” or “•” are removed automatically.</p>
            {(form.features || []).length > 0 && (
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 list-disc pl-5 text-sm text-neutral-700">
                {form.features.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Photos</h2>
          <MediaUploader value={form.gallery || []} onChange={(v) => set('gallery', v)} label="Property Gallery (upload multiple images)" />
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Video Tour (optional)</h2>
          <MediaUploader value={form.videos || []} onChange={(v) => set('videos', v)} accept="video/*" kind="video" label="Property Videos (upload multiple)" />
        </div>

        <div className="flex items-center justify-end gap-3 sticky bottom-4">
          <Link to="/admin/properties"><Button type="button" variant="outline">Cancel</Button></Link>
          <Button type="submit" disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isEdit ? 'Save Changes' : 'Create Property'}</Button>
        </div>
      </form>
    </div>
  );
}
